import { defineStore } from 'pinia'
import { convertArrayToObject, mergeObjects } from 'src/utils'
import { useCartStore } from 'src/stores/cart'

export const useAddressStore = defineStore('address', {
  state: () => ({
    addresses: {},
    billingAddresses: {},
    selectedAddress: {},
    loading: true,
    addressMapping: {},
    alertInfo: {},
  }),
  getters: {
    newAddressId(state) {
      return (oldId) => state.addressMapping[oldId]
    },
    defaultAddress(state) {
      let defaultAddress = null

      for (let key in state.addresses) {
        if (state.addresses[key].isDefault) {
          defaultAddress = state.addresses[key]
          break
        } else if (!defaultAddress) {
          defaultAddress = state.addresses[key]
        }
      }

      return defaultAddress
    },
    selectedAddressForFulfillmentCode(state) {
      return (fulfillmentCode) => {
        const cartStore = useCartStore()
        let cartDetail = cartStore.currentCartDetails
        let selectedAddress = state.selectedAddress[fulfillmentCode]
        if (selectedAddress) {
          return selectedAddress
        }

        if (cartDetail) {
          for (let fId in cartDetail.fulfillments) {
            let fulfillment = cartDetail.fulfillments[fId]
            if (
              fulfillment.codeName == fulfillmentCode &&
              fulfillment.addressID
            ) {
              for (let key in state.addresses) {
                if (key == fulfillment.addressID) {
                  return state.addresses[key]
                }
              }
            }
          }
        }

        return this.defaultAddress
      }
    },
    getAddressByAddressID(state) {
      return (addressId) => {
        let address = null
        for (let key in state.addresses) {
          if (key == addressId) address = state.addresses[key]
        }
        return address
      }
    },
    alertData: (state) => (alertKey) => state.alertInfo[alertKey] || null,
    getAddressByCustomerAddressID(state) {
      return (customerAddressesID) => {
        let selectedAddress = null
        for (let key in state.addresses) {
          let address = state.addresses[key]
          if (address && address.customerAddressesID == customerAddressesID) {
            selectedAddress = address
            break
          } else continue
        }
        return selectedAddress
      }
    },
  },
  actions: {
    async getBillingAddresses(params = {}) {
      try {
        let { data } = await this.$axiosInstance.get('/Customer/Address/', {
          params: {
            global: true,
            DisablePaging: true,
            addressType: 1,
            ...params,
          },
        })

        this.billingAddresses = convertArrayToObject(
          data.data.items,
          'customerAddressesID'
        )
      } catch (error) {}
    },
    async getCustomerAddresses(params = {}, isSave = true) {
      try {
        this.loading = true
        let { data } = await this.$axiosInstance.get('/Customer/Address/', {
          params: {
            DisablePaging: true,
            addressType: 0,
            global: true,
            ...params,
          },
        })

        if (isSave && data?.success && data?.data) {
          this.addresses = convertArrayToObject(data.data.items, 'addressId')
        }
        this.loading = false
        return data
      } catch (error) {
        return error
      }
    },
    async addOrUpdateAddress(address) {
      try {
        let response
        if (address.customerAddressesID) {
          response = await this.$axiosInstance.put(
            `/Customer/Address/${address.customerAddressesID}`,
            address
          )
        } else {
          response = await this.$axiosInstance.post(
            '/Customer/Address/',
            address
          )
        }

        if (response.data.success && response.data.data) {
          if (address.type == 1) {
            const resAddress = response.data.data.address || response.data.data
            if (resAddress.isDefault) {
              for (let key in this.billingAddresses) {
                this.billingAddresses[key].isDefault = false
              }
            }

            this.billingAddresses = Object.assign({}, this.billingAddresses, {
              [resAddress.addressId]: resAddress,
            })
          } else {
            const resAddress = response.data.data.address || response.data.data
            if (resAddress.isDefault) {
              for (let key in this.addresses) {
                this.addresses[key].isDefault = false
              }
            }

            this.addresses = Object.assign({}, this.addresses, {
              [resAddress.addressId]: resAddress,
            })

            const deletedAddressId = response.data.data.deletedAddressId
            if (deletedAddressId) {
              delete this.addresses[deletedAddressId]
              this.addressMapping = Object.assign(
                {},
                {
                  [deletedAddressId]: response.data.data.address.addressId,
                }
              )
            }
          }
        }

        return response.data.data
      } catch (error) {
        return false
      }
    },
    async deleteAddress(address) {
      if (address && address.customerAddressesID) {
        try {
          let { data } = await this.$axiosInstance.delete(
            `/Customer/Address/${address.customerAddressesID}`
          )

          if (data && data.data) {
            delete this.addresses[address.addressId]
          }
          return data
        } catch (error) {
          return error
        }
      }
    },
    async UpdateDefaultAddress(address) {
      try {
        let { data } = await this.$axiosInstance.put(
          '/Customer/Address/UpdateDefaultAddress',
          {
            customerAddressId: address.customerAddressesID,
          }
        )

        if (data?.data) {
          const resAddress = mergeObjects(address, { isDefault: true })
          if (resAddress.isDefault) {
            for (let key in this.addresses) {
              this.addresses[key].isDefault = false
            }
          }

          this.addresses = Object.assign({}, this.addresses, {
            [resAddress.addressId]: resAddress,
          })
          return true
        }
        return false
      } catch (error) {
        return false
      }
    },
    setLoading(loading) {
      this.loading = loading
    },
    setAddressAlert({ alertKey, alertData }) {
      if (typeof alertData === 'object' && alertData !== null)
        this.alertInfo = { ...this.alertInfo, [alertKey]: alertData }
      else this.alertInfo = { ...this.alertInfo, [alertKey]: null }
    },
    setFulfillmentAddress({ address, fulfillmentCode }) {
      this.selectedAddress = Object.assign({}, this.selectedAddress, {
        [fulfillmentCode]: address,
      })
    },
    setField({ field, value }) {
      this[field] = value
    },
  },
})
